import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ImageUpload } from "@/components/ImageUpload";

const emptyCourse = {
    title: "",
    description: "",
    duration: "",
    level: "Beginner",
    price: 0,
    image_url: "",
    highlights: [],
    lessons: [],
    long_description: "",
    offline_surcharge: 1500,
    offline_capacity: 20,
    category_id: null,
    medium: "",
    start_date: "",
    end_date: "",
    total_duration: "",
    batch_timing: "",
    next_batch_date: "",
    guidelines: [],
    is_membership_only: false,
};

function uid() { return `l-${Math.random().toString(36).slice(2, 10)}`; }

function LessonsBuilder({ lessons, onChange }) {
    const add = () => onChange([...(lessons || []), { id: uid(), title: "", duration: "", description: "", video_url: "", is_preview: false }]);
    const update = (i, patch) => onChange(lessons.map((l, idx) => idx === i ? { ...l, ...patch } : l));
    const remove = (i) => onChange(lessons.filter((_, idx) => idx !== i));
    return (
        <div className="space-y-2" data-testid="lessons-builder">
            {(lessons || []).map((L, i) => (
                <div key={L.id || i} className="rounded-xl bg-white border border-gray-100 p-3 grid sm:grid-cols-12 gap-2 items-start" data-testid={`lesson-row-${i}`}>
                    <input className="sm:col-span-3 rounded-lg border border-input h-9 px-3 text-sm bg-background" placeholder="Title" value={L.title} onChange={(e) => update(i, { title: e.target.value })} data-testid={`lesson-title-${i}`} />
                    <input className="sm:col-span-2 rounded-lg border border-input h-9 px-3 text-sm bg-background" placeholder="Duration" value={L.duration} onChange={(e) => update(i, { duration: e.target.value })} />
                    <input className="sm:col-span-3 rounded-lg border border-input h-9 px-3 text-sm bg-background" placeholder="Description" value={L.description} onChange={(e) => update(i, { description: e.target.value })} />
                    <input className="sm:col-span-3 rounded-lg border border-input h-9 px-3 text-sm bg-background" placeholder="YouTube/Vimeo URL" value={L.video_url || ""} onChange={(e) => update(i, { video_url: e.target.value })} data-testid={`lesson-video-url-${i}`} />
                    <div className="sm:col-span-1 flex items-center justify-end gap-1">
                        <label className="text-[10px] inline-flex items-center gap-1 cursor-pointer" title="Free preview — shown to non-enrolled visitors">
                            <input type="checkbox" checked={!!L.is_preview} onChange={(e) => update(i, { is_preview: e.target.checked })} data-testid={`lesson-preview-${i}`} />
                            preview
                        </label>
                        <button type="button" onClick={() => remove(i)} className="h-7 w-7 rounded-full border text-accent-700 hover:bg-accent-50 inline-flex items-center justify-center" data-testid={`lesson-remove-${i}`}>×</button>
                    </div>
                </div>
            ))}
            <button type="button" onClick={add} className="rounded-full bg-brand-50 text-brand-700 px-4 h-9 text-sm font-semibold hover:bg-brand-100" data-testid="lesson-add-btn">
                + Add lesson
            </button>
        </div>
    );
}

export default function CourseEditor({ open = true, onOpenChange, initial, onSaved, page = false }) {
    const isEdit = Boolean(initial?.id);
    const [form, setForm] = useState(emptyCourse);
    const [highlightsRaw, setHighlightsRaw] = useState("");
    const [guidelinesRaw, setGuidelinesRaw] = useState("");
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        if (open) {
            const seed = initial ? { ...emptyCourse, ...initial } : emptyCourse;
            setForm(seed);
            setHighlightsRaw((seed.highlights || []).join("\n"));
            setGuidelinesRaw((seed.guidelines || []).join("\n"));
            api.get("/admin/course-categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
        }
    }, [open, initial]);

    const close = () => onOpenChange?.(false);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: (name === "price" || name === "offline_surcharge" || name === "offline_capacity") ? Number(value) : value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                price: Number(form.price),
                offline_surcharge: Number(form.offline_surcharge ?? 1500),
                offline_capacity: Number(form.offline_capacity ?? 20),
                highlights: highlightsRaw.split("\n").map((s) => s.trim()).filter(Boolean),
                guidelines: guidelinesRaw.split("\n").map((s) => s.trim()).filter(Boolean),
                lessons: form.lessons || [],
                category_id: form.category_id || null,
                medium: form.medium || null,
                start_date: form.start_date || null,
                end_date: form.end_date || null,
                total_duration: form.total_duration || null,
                batch_timing: form.batch_timing || null,
                next_batch_date: form.next_batch_date || null,
                is_membership_only: !!form.is_membership_only,
            };
            if (isEdit) {
                await api.patch(`/admin/courses/${initial.id}`, payload);
                toast.success("Course updated");
            } else {
                await api.post("/admin/courses", payload);
                toast.success("Course created");
            }
            onSaved?.();
            close();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const formBody = (
        <form onSubmit={onSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Category</Label>
                            <Select
                                value={form.category_id || "__none__"}
                                onValueChange={(val) =>
                                    setForm((f) => ({ ...f, category_id: val === "__none__" ? null : val }))
                                }
                            >
                                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="course-form-category">
                                    <SelectValue placeholder="Select a category" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">— No category —</SelectItem>
                                    {categories.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {categories.length === 0 && (
                                <p className="text-[11px] text-ink-700 mt-1">No categories yet — create them via "Manage categories" on the Courses list.</p>
                            )}
                        </div>
                        <div>
                            <Label>Title</Label>
                            <Input name="title" value={form.title} onChange={onChange} required className="mt-1.5 rounded-xl" data-testid="course-form-title" />
                        </div>
                        <div>
                            <Label>Level</Label>
                            <Input name="level" value={form.level} onChange={onChange} required className="mt-1.5 rounded-xl" />
                        </div>
                        <div>
                            <Label>Price (₹)</Label>
                            <Input type="number" name="price" value={form.price} onChange={onChange} required min={0} className="mt-1.5 rounded-xl" />
                        </div>
                    </div>

                    <div className="rounded-2xl bg-paper-alt border border-gray-100 p-4">
                        <Label className="font-heading text-base">Feature image</Label>
                        <p className="text-xs text-ink-700 mb-3">Upload or drag-and-drop an image — shown as the course's primary visual on cards, the hero, and the detail page.</p>
                        <ImageUpload
                            value={form.image_url}
                            onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
                            testIdPrefix="course-feature-image"
                        />
                    </div>

                    <div>
                        <Label>Short description</Label>
                        <Textarea name="description" value={form.description} onChange={onChange} required rows={2} className="mt-1.5 rounded-xl" data-testid="course-form-short-description" />
                    </div>
                    <div>
                        <Label>Long description</Label>
                        <Textarea name="long_description" value={form.long_description || ""} onChange={onChange} rows={4} className="mt-1.5 rounded-xl" data-testid="course-form-long-description" />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Medium of communication</Label>
                            <Input name="medium" value={form.medium || ""} onChange={onChange} placeholder="e.g. English & Hindi" className="mt-1.5 rounded-xl" data-testid="course-form-medium" />
                        </div>
                        <div>
                            <Label>Duration label (legacy)</Label>
                            <Input name="duration" value={form.duration} onChange={onChange} required placeholder="e.g. 8 weeks · 24 sessions" className="mt-1.5 rounded-xl" />
                        </div>
                    </div>

                    <div className="rounded-2xl bg-paper-alt p-4 space-y-4">
                        <div className="font-heading text-base">Schedule</div>
                        <div className="grid sm:grid-cols-3 gap-4">
                            <div>
                                <Label>Start date</Label>
                                <Input type="date" name="start_date" value={form.start_date || ""} onChange={onChange} className="mt-1.5 rounded-xl" data-testid="course-form-start-date" />
                            </div>
                            <div>
                                <Label>End date</Label>
                                <Input type="date" name="end_date" value={form.end_date || ""} onChange={onChange} className="mt-1.5 rounded-xl" data-testid="course-form-end-date" />
                            </div>
                            <div>
                                <Label>Total duration (free text)</Label>
                                <Input name="total_duration" value={form.total_duration || ""} onChange={onChange} placeholder="e.g. 60 hours · 8 weeks" className="mt-1.5 rounded-xl" data-testid="course-form-total-duration" />
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label>Batch timing (for offline)</Label>
                                <Input name="batch_timing" value={form.batch_timing || ""} onChange={onChange} placeholder="e.g. Mon–Fri · 6:00–7:30 AM" className="mt-1.5 rounded-xl" data-testid="course-form-batch-timing" />
                            </div>
                            <div>
                                <Label>Upcoming next batch</Label>
                                <Input type="date" name="next_batch_date" value={form.next_batch_date || ""} onChange={onChange} className="mt-1.5 rounded-xl" data-testid="course-form-next-batch-date" />
                            </div>
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Offline Surcharge (₹)</Label>
                            <Input type="number" name="offline_surcharge" value={form.offline_surcharge ?? 1500} onChange={onChange} min={0} className="mt-1.5 rounded-xl" data-testid="course-form-offline-surcharge" />
                            <p className="mt-1 text-xs text-ink-700">Added to the online price for in-studio enrollment.</p>
                        </div>
                        <div>
                            <Label>Studio Capacity</Label>
                            <Input type="number" name="offline_capacity" value={form.offline_capacity ?? 20} onChange={onChange} min={0} className="mt-1.5 rounded-xl" data-testid="course-form-offline-capacity" />
                            <p className="mt-1 text-xs text-ink-700">Max offline seats. Beyond this, the offline button shows "Sold Out".</p>
                        </div>
                    </div>

                    <div>
                        <Label>Highlights (one per line)</Label>
                        <Textarea value={highlightsRaw} onChange={(e) => setHighlightsRaw(e.target.value)} rows={3} className="mt-1.5 rounded-xl" data-testid="course-form-highlights" />
                    </div>
                    <div>
                        <Label>Guidelines (one per line)</Label>
                        <Textarea value={guidelinesRaw} onChange={(e) => setGuidelinesRaw(e.target.value)} rows={4} placeholder="e.g.&#10;Wear comfortable clothing&#10;Bring your own mat&#10;Arrive 10 minutes before the session" className="mt-1.5 rounded-xl" data-testid="course-form-guidelines" />
                        <p className="mt-1 text-xs text-ink-700">Shown as a checklist on the course detail page.</p>
                    </div>

                    <div className="rounded-2xl bg-paper-alt p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <Label className="font-heading text-base">Lessons</Label>
                            <span className="text-xs text-ink-700">Video URLs are only revealed to enrolled users (or anyone for "preview" lessons).</span>
                        </div>
                        <LessonsBuilder
                            lessons={form.lessons || []}
                            onChange={(lessons) => setForm((f) => ({ ...f, lessons }))}
                        />
                    </div>

                    <label className="flex items-start gap-3 rounded-2xl bg-paper-alt border border-gray-100 p-4 cursor-pointer" data-testid="course-form-membership-only-row">
                        <input
                            type="checkbox"
                            checked={!!form.is_membership_only}
                            onChange={(e) => setForm((f) => ({ ...f, is_membership_only: e.target.checked }))}
                            className="mt-1"
                            data-testid="course-form-membership-only"
                        />
                        <div>
                            <div className="font-semibold text-sm">Members-only course</div>
                            <div className="text-xs text-ink-700">Active members enroll for free. Non-members are blocked from purchase.</div>
                        </div>
                    </label>

                    <div className="flex items-center justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={close} className="rounded-full">Cancel</Button>
                        <Button type="submit" disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="course-form-save">
                            {saving ? "Saving…" : isEdit ? "Save changes" : "Create course"}
                        </Button>
                    </div>
                </form>
    );

    const title = isEdit ? "Edit course" : "New course";

    if (page) {
        return (
            <div data-testid="course-editor-page-body">
                <h2 className="font-heading text-2xl mb-1">{title}</h2>
                <p className="text-sm text-ink-700 mb-4">Fill in the course details.</p>
                {formBody}
            </div>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-3xl rounded-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">{title}</DialogTitle>
                    <DialogDescription>Fill in the course details.</DialogDescription>
                </DialogHeader>
                {formBody}
            </DialogContent>
        </Dialog>
    );
}
